import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonLanding } from 'components';
import { animationParams, animationVariants } from 'fragments';
import { dataCollectionFeatures } from './constants';
import { landingImages, Svg } from 'assets';
import { IsImageLoaded } from 'utils';
import { IntegrationsSection, INTEGRATIONS_THEMES, ReadyTransform } from '../../common';

export const DataCollectionFragment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const imageLoaded = IsImageLoaded(landingImages.clinicalHeroImg);

    useEffect(() => {
        if (location.hash) {
            const timer = setTimeout(() => {
                const el = document.querySelector(location.hash);
                el?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [location]);

    return (
        <div className="clinical-page-wrapper">
            {/* Hero Section */}
            <section className="clinical-hero">
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text-block">
                            <span className="hero-badge">Track. Analyze. Grow. All in one place</span>
                            <div style={{ overflow: 'hidden' }}>
                                <motion.h1
                                    className="hero-title"
                                    {...animationParams}
                                    variants={animationVariants.textVariant}
                                >
                                    Real-Time ABA <br /> <span
                                    style={{ color: '#9E77ED' }}>Data Collection</span><br /> That Moves With You
                                </motion.h1>
                            </div>

                            <motion.div
                                {...animationParams}
                                variants={animationVariants.staggerContainerVariant}
                            >
                                <motion.p
                                    className="hero-subtitle"
                                    variants={animationVariants.leftToRightVariant}
                                >
                                    Collect and review data instantly - wherever sessions happen. TherapyLake keeps you
                                    connected to progress, trends, and insights across every learner.
                                </motion.p>
                            </motion.div>

                            <div className="hero-btn-container">
                                <ButtonLanding
                                    text="See Practice Management"
                                    onClick={() => navigate('/practice')}
                                    arrowIcon
                                />
                            </div>
                        </div>
                        <div className="hero-image-block">
                            <div className="hero-image-placeholder">
                                <motion.img
                                    src={landingImages.clinicalHeroImgSmall}
                                    {...animationParams}
                                    variants={animationVariants.cardsVariant}
                                    alt="banner"
                                />

                                <motion.img
                                    src={landingImages.clinicalHeroImg}
                                    alt="banner"
                                    style={{
                                        opacity: imageLoaded ? 1 : 0,
                                        transition: 'opacity 0.5s ease-in-out',
                                        visibility: imageLoaded ? 'visible' : 'hidden',
                                        zIndex: imageLoaded ? 1 : 0,
                                    }}
                                    {...animationParams}
                                    variants={animationVariants.cardsVariant}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="clinical-features">
                <div className="container">
                    {dataCollectionFeatures.map(({ BadgeIcon, ...feature }, index) => {
                        const isEven = index % 2 === 0;
                        const featureImageLoaded = IsImageLoaded(feature?.image);

                        return (
                            <div
                                key={feature?.id}
                                id={feature?.id}
                                className={`feature-row ${isEven ? 'row-even' : 'row-odd'}`}
                            >
                                <div className="feature-content">
                                    {BadgeIcon &&
                                        <div className="feature-badge__icon">
                                            <BadgeIcon />
                                        </div>
                                    }
                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.p
                                            className="feature-badge__label"
                                            {...animationParams}
                                            variants={animationVariants.titleVariantMobile}
                                        >
                                            {feature?.badgeLabel}
                                        </motion.p>
                                    </div>

                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.h2
                                            className="feature-title"
                                            {...animationParams}
                                            variants={animationVariants.titleVariantMobile}
                                        >
                                            {feature.title}
                                        </motion.h2>
                                    </div>

                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.p
                                            className="feature-text"
                                            {...animationParams}
                                            variants={animationVariants.titleVariant}
                                        >
                                            {feature.text}
                                        </motion.p>
                                    </div>

                                    <motion.ul
                                        className="feature-bullets"
                                        {...animationParams}
                                        variants={animationVariants.staggerContainerVariant}
                                    >
                                        {feature.bullets.map((bullet, idx) => (
                                            <motion.li
                                                key={idx}
                                                variants={animationVariants.leftToRightVariant}
                                            >
                                                <span className="bullet-icon">
                                                    <Svg.Check2Svg />
                                                </span>
                                                <span className="bullet-text">{bullet}</span>
                                            </motion.li>
                                        ))}
                                    </motion.ul>
                                </div>
                                <div className="feature-image">
                                    <motion.img
                                        src={feature?.imageSmall}
                                        {...animationParams}
                                        variants={animationVariants.cardsVariant}
                                        alt="banner"
                                    />

                                    <motion.img
                                        src={feature?.image}
                                        alt="banner"
                                        style={{
                                            opacity: featureImageLoaded ? 1 : 0,
                                            transition: 'opacity 0.5s ease-in-out',
                                            visibility: featureImageLoaded ? 'visible' : 'hidden',
                                            zIndex: featureImageLoaded ? 1 : 0,
                                        }}
                                        {...animationParams}
                                        variants={animationVariants.cardsVariant}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <IntegrationsSection themeConfig={INTEGRATIONS_THEMES.DataCollection} />

            <ReadyTransform className='ready-transform-data-collection' />
        </div>
    );
};
